// OrdersSelectInput.tsx
import React from "react";
import { useFormik } from "formik";
import Select, { components } from "react-select";
import "../assets/css/SignageOrders.css"

/* ------------------------------------------------------------------ */
/* Types & shared helpers                                              */
/* ------------------------------------------------------------------ */

type OptionBase = { value: string; label: string };

export type SelectProProps<T extends OptionBase> = {
  /* Required */
  formik: ReturnType<typeof useFormik<any>>;
  name: string;

  /* UI */
  label?: React.ReactNode;
  required?: boolean;
  placeholder?: string;

  /* Behavior */
  disabled?: boolean;
  isDisabled?: boolean; // alias for convenience
  onBlur?: (e: React.FocusEvent<any>) => void;

  /* Options (generic) */
  options: T[];
  /** If omitted, will derive from formik.values[name] */
  value?: T | null;
  onChange?: (opt: T | null) => void;

  /* Menu layout */
  menuMaxHeight?: number;
  menuPortalZIndex?: number;
};

type BaseFieldProps = {
  formik: ReturnType<typeof useFormik<any>>;
  name: string;
  label?: React.ReactNode;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
};

type InputProProps = BaseFieldProps & {
  type?: React.HTMLInputTypeAttribute;

  /* Optional controlled props for edit screens */
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  readOnly?: boolean;
};

const SELECT_HEIGHT = 42;
const isEmailInput = (name?: string, type?: React.HTMLInputTypeAttribute) =>
  type === "email" || /email/i.test(String(name || ""));

const commonTheme = (t: any) => ({
  ...t,
  colors: {
    ...t.colors,
    primary: "#9ca3af",
    primary25: "rgba(156,163,175,0.15)",
    primary50: "rgba(156,163,175,0.25)",
  },
});
const centeredCommonStyles = (hasError: boolean) => ({
  control: (base: any, state: any) => ({
    ...base,
    minHeight: SELECT_HEIGHT,
    height: SELECT_HEIGHT,
    borderRadius: 10,
    alignItems: "center",
    backgroundColor: state.isFocused ? "#fafafa" : "#f5f6fa",
    borderWidth: 2,
    borderStyle: "solid",
    borderColor: hasError ? "#747981" : state.isFocused ? "#9ca3af" : "#c9ced6",
    boxShadow: "none",
    outline: "none",
    transition: "all .2s ease-in-out",
    ":hover": { borderColor: hasError ? "#747981" : "#9ca3af", backgroundColor: "#fafafa" },
  }),
  valueContainer: (b: any) => ({
    ...b,
    padding: "0 12px",
    minHeight: SELECT_HEIGHT,
    display: "flex",
    alignItems: "center",
    overflow: "hidden",     // ✨ allow truncation
    flexWrap: "nowrap",
    minWidth: 0,            // ✨ critical for ellipsis in flex
  }),
  indicatorsContainer: (b: any) => ({
    ...b,
    paddingRight: 10,
    minHeight: SELECT_HEIGHT,
    display: "flex",
    alignItems: "center",
  }),
  dropdownIndicator: (p: any) => ({ ...p, display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }),
  indicatorSeparator: () => ({ display: "none" }),
  placeholder: (b: any) => ({ ...b, color: "#6b7280", margin: 0, display: "flex", alignItems: "center" }),
  singleValue: (b: any) => ({
    ...b,
    color: "#111827",
    margin: 0,
    display: "block",
    overflow: "hidden",      
    textOverflow: "ellipsis",  
    whiteSpace: "nowrap",       
    maxWidth: "100%",         
  }),
  input: (b: any) => ({ ...b, margin: 0, padding: 0 }),
  // show full text in the menu (no ellipsis)
  option: (b: any) => ({ ...b, whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3 }),
  menuList: (b: any) => ({ ...b, padding: 0 }),
  menu: (b: any) => ({ ...b, borderRadius: 10, overflow: "hidden" }),
});


// const centeredCommonStyles = (hasError: boolean) => ({
//   control: (base: any, state: any) => ({
//     ...base,
//     minHeight: SELECT_HEIGHT,
//     height: SELECT_HEIGHT,           // fixed for single-select
//     borderRadius: 10,
//     alignItems: "center",
//     backgroundColor: state.isFocused ? "#fafafa" : "#f5f6fa",
//     borderWidth: 2,
//     borderStyle: "solid",
//     borderColor: hasError ? "#747981" : state.isFocused ? "#9ca3af" : "#c9ced6",
//     boxShadow: "none",
//     outline: "none",
//     transition: "all .2s ease-in-out",
//     ":hover": {
//       borderColor: hasError ? "#747981" : "#9ca3af",
//       backgroundColor: "#fafafa",
//     },
//   }),
//   valueContainer: (b: any) => ({
//     ...b,
//     padding: "0 12px",
//     minHeight: SELECT_HEIGHT,
//     display: "flex",
//     alignItems: "center",
//   }),
//   indicatorsContainer: (b: any) => ({
//     ...b,
//     paddingRight: 10,
//     minHeight: SELECT_HEIGHT,
//     display: "flex",
//     alignItems: "center",
//   }),
//   dropdownIndicator: (provided: any) => ({
//     ...provided,
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "center",
//     height: "100%",
//   }),
//   indicatorSeparator: () => ({ display: "none" }),
//   placeholder: (b: any) => ({
//     ...b,
//     color: "#6b7280",
//     margin: 0,
//     display: "flex",
//     alignItems: "center",
//   }),
//   singleValue: (b: any) => ({
//     ...b,
//     color: "#111827",
//     margin: 0,
//     display: "flex",
//     alignItems: "center",
//   }),
//   input: (b: any) => ({
//     ...b,
//     margin: 0,
//     padding: 0,
//   }),
//   menu: (b: any) => ({ ...b, borderRadius: 10, overflow: "hidden" }),
// });

const DropdownIndicator = (props: any) => (
  <components.DropdownIndicator {...props}>
    <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
  </components.DropdownIndicator>
);

// add this helper near DropdownIndicator
const TruncatedSingleValue = (props: any) => {
  const fullLabel = props.data?.label ?? "";
  return (
    <components.SingleValue {...props}>
      <span
        title={typeof fullLabel === "string" ? fullLabel : ""}
        style={{
          display: "block",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {props.children}
      </span>
    </components.SingleValue>
  );
};

/* ------------------------------------------------------------------ */
/* 1) Select — works for ADD + EDIT                                    */
/* ------------------------------------------------------------------ */

// export function UIReactSelectPro<T extends OptionBase>({
//   formik,
//   name,
//   label,
//   required,
//   placeholder = "- Select -",
//   disabled,
//   isDisabled,
//   onBlur,
//   options,
//   value,
//   onChange,
//   menuMaxHeight = 240,
//   menuPortalZIndex = 20,
// }: SelectProProps<T>) {
//   const hasError = Boolean(formik.touched?.[name] && formik.errors?.[name]);
//   const error = formik.errors?.[name] as string | undefined;

//   // If caller doesn't pass a value, derive from Formik
//   const derived: T | null =
//     value !== undefined
//       ? value
//       : ((options.find((o) => o.value === (formik.values as any)?.[name]) as T) ||
//          null);

//   // Anchor so the menu renders directly under the field (no drift in modals/tables)
//   const anchorRef = React.useRef<HTMLDivElement>(null);

//   const styles = {
//     ...centeredCommonStyles(hasError),
//     menuPortal: (base: any) => ({
//       ...base,
//       position: "absolute",
//       zIndex: menuPortalZIndex,
//       inset: "auto",
//       overflow: "visible",
//       width: "100%",
//       left: 0,
//     }),
//     menu: (b: any) => ({
//       ...centeredCommonStyles(hasError).menu(b),
//       maxHeight: menuMaxHeight,
//       overflowY: "auto",
//       marginTop: 4,
//       width: "100%",
//       minWidth: "100%",
//       maxWidth: "100%",
//     }),
//   };

//   return (
//     <div className="mb-3 rs-pop-anchor" ref={anchorRef}>
//       {label && (
//         <label className="form-label" htmlFor={name}>
//           {label} {required && <span className="text-danger">*</span>}
//         </label>
//       )}

//       <Select<T, false>
//         inputId={name}
//         name={name}
//         classNamePrefix="rs"
//         value={derived}
//         options={options}
//         placeholder={placeholder}
//         isDisabled={Boolean(disabled ?? isDisabled)}
//         styles={styles}
//         theme={commonTheme}
//         components={{ DropdownIndicator, SingleValue: TruncatedSingleValue }}
//         /* Anchor the menu to this control to keep it aligned + full width */
//         menuPortalTarget={anchorRef.current ?? undefined}
//         menuPosition="absolute"
//         menuPlacement="bottom"
//         menuShouldScrollIntoView={false}
//         onChange={(opt) => {
//           const sel = (opt ?? null) as T | null;
//           // Always keep Formik in sync with just the value
//           formik.setFieldValue(name, sel?.value ?? "");
//           // Bubble full option back (for cases like {value,label,cost})
//           onChange?.(sel);
//         }}
//         onBlur={(e) => {
//           // Formik touched
//           formik.setFieldTouched(name, true, true);
//           // Bubble
//           onBlur?.(e);
//         }}
//       />

//       {hasError ? <div className="invalid-feedback d-block">{error}</div> : null}

//       {/* CSS helpers for perfect anchoring/alignment */}
//       <style>{`
//         .rs-pop-anchor { position: relative; width: 100%; overflow: visible; }
//         .rs-pop-anchor .rs__menu,
//         .rs-pop-anchor .rs__menu-portal { width: 100% !important; left: 0 !important; }
//       `}</style>
//     </div>
//   );
// }

export function UIReactSelectPro<T extends OptionBase>({
  formik,
  name,
  label,
  required,
  placeholder = "- Select -",
  disabled,
  isDisabled,
  onBlur,
  options,
  value,
  onChange,
  menuMaxHeight = 240,
  menuPortalZIndex = 20,
}: SelectProProps<T>) {
  const touched = formik.touched?.[name];
  const error   = formik.errors?.[name] as string | undefined;

  // Prefer caller value; otherwise derive from Formik by matching option.value
  const derived: T | null =
    value !== undefined
      ? (value as T | null)
      : ((options.find(o => o.value === (formik.values as any)?.[name]) as T) || null);

  // NEW: treat non-empty select as valid (prevents showing old error)
  const rawVal   = value !== undefined ? (value as any)?.value : (formik.values as any)?.[name];
  const isEmpty  = rawVal === undefined || rawVal === null || String(rawVal).trim() === "";
  const hasError = Boolean(touched && error && isEmpty);

  // NEW: clear stale field error once a value appears (no extra blur needed)
  React.useEffect(() => {
    if (touched && error && !isEmpty) {
      formik.setFieldError(name, undefined);
    }
    // eslint-disable-next-line
  }, [rawVal]);

  const anchorRef = React.useRef<HTMLDivElement>(null);

  const styles = {
    ...centeredCommonStyles(hasError),
    menuPortal: (base: any) => ({
      ...base,
      position: "absolute",
      zIndex: menuPortalZIndex,
      inset: "auto",
      overflow: "visible",
      width: "100%",
      left: 0,
    }),
    menu: (b: any) => ({
      ...centeredCommonStyles(hasError).menu(b),
      maxHeight: menuMaxHeight,
      overflow: "hidden",
      marginTop: 4,
      width: "100%",
      minWidth: "100%",
      maxWidth: "100%",
    }),
    menuList: (b: any) => ({
      ...b,
      padding: 0,
      maxHeight: menuMaxHeight,
      overflowY: "auto",
    }),
  };

  return (
    <div className="mb-3 rs-pop-anchor" ref={anchorRef}>
      {label && (
        <label className="form-label" htmlFor={name}>
          {label} {required && <span className="text-danger">*</span>}
        </label>
      )}

      <Select<T, false>
        inputId={name}
        name={name}
        classNamePrefix="rs"
        value={derived}
        options={options}
        placeholder={placeholder}
        isDisabled={Boolean(disabled ?? isDisabled)}
        styles={styles}
        theme={commonTheme}
        components={{ DropdownIndicator, SingleValue: TruncatedSingleValue }}
        menuPortalTarget={anchorRef.current ?? undefined}
        menuPosition="absolute"
        menuPlacement="bottom"
        menuShouldScrollIntoView={false}
        onChange={(opt) => {
          const sel = (opt ?? null) as T | null;

          // Keep Formik in sync with just the value (and validate immediately)
          formik.setFieldValue(name, sel?.value ?? "", true);

          // NEW: proactively clear touched + error to remove stale state
          formik.setFieldTouched(name, false, false);
          formik.setFieldError(name, undefined);

          // Bubble full option
          onChange?.(sel);
        }}
        onBlur={(e) => {
          formik.setFieldTouched(name, true, true);
          onBlur?.(e);
        }}
      />

      {hasError ? <div className="invalid-feedback d-block">{error}</div> : null}

      <style>{`
        .rs-pop-anchor { position: relative; width: 100%; overflow: visible; }
        .rs-pop-anchor .rs__menu,
        .rs-pop-anchor .rs__menu-portal { width: 100% !important; left: 0 !important; }
      `}</style>
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* 2) Input — works for ADD + EDIT                                     */
/* ------------------------------------------------------------------ */

// export function UIInputPro({
//   formik,
//   name,
//   label,
//   required,
//   placeholder,
//   disabled,
//   readOnly,
//   type = "text",
//   value,
//   onChange,
//   onBlur,
// }: InputProProps) {
//   const [hover, setHover] = React.useState(false);
//   const [focus, setFocus] = React.useState(false);

//   const touched = (formik.touched as any)?.[name];
//   const error = (formik.errors as any)?.[name];
//   const hasError = !!(touched && error);

//   const bgColor = hover || focus ? "#fafafa" : "#f5f6fa";
//   const borderColor = hasError ? "#747981" : hover || focus ? "#9ca3af" : "#c9ced6";

//   const style: React.CSSProperties = {
//     width: "100%",
//     height: 39,
//     borderRadius: 10,
//     padding: "10px 12px",
//     borderWidth: 2,
//     borderStyle: "solid",
//     borderColor,
//     backgroundColor: bgColor,
//     outline: "none",
//     transition: "all .2s ease-in-out",
//     boxShadow: "none",
//     color: "#111827",
//   };

//   // Prefer controlled value if provided; otherwise, use Formik state
//   const resolvedValue =
//     value !== undefined ? value : (formik.values as any)?.[name] ?? "";

//   return (
//     <div className="mb-3">
//       {label && (
//         <label className="form-label" htmlFor={name}>
//           {label} {required && <span className="text-danger">*</span>}
//         </label>
//       )}

//       <input
//         id={name}
//         name={name}
//         className={`form-control ${hasError ? "is-invalid" : ""}`}
//         style={style}
//         type={type}
//         value={resolvedValue}
//         onChange={(e) => {
//           onChange?.(e); // bubble
//           if (!onChange) formik.handleChange(e); // keep Formik in sync when uncontrolled
//         }}
//         onFocus={() => setFocus(true)}
//         onBlur={(e) => {
//           setFocus(false);
//           onBlur?.(e); // bubble
//           if (!onBlur) formik.handleBlur(e); // Formik touched when uncontrolled
//         }}
//         onMouseEnter={() => setHover(true)}
//         onMouseLeave={() => setHover(false)}
//         placeholder={placeholder}
//         aria-invalid={hasError}
//         disabled={!!disabled}
//         readOnly={!!readOnly}
//         aria-readonly={!!readOnly}
//         autoComplete="off"
//       />

//       {hasError ? <div className="invalid-feedback d-block">{error}</div> : null}
//     </div>
//   );
// }



















































// // --- PRO variants with same look but more flexible props ---
// import React, { useEffect, useMemo, useState } from "react";
// import { useFormik } from "formik";
// import * as Yup from "yup";
// import { components } from "react-select";
// import Select from "react-select";

// type OptionBase = { value: string; label: string };

// // Generic props
// type SelectProPropsAdd<T extends OptionBase> = {
//   formik: any;
//   name: string;
//   label: React.ReactNode;
//   required?: boolean;
//   placeholder?: string;
//   disabled?: boolean;
//   options: T[];
//   value?: T | null;
//   onChange?: (opt: T | null) => void;
//   menuMaxHeight?: number;
//   menuPortalZIndex?: number;
// };

// export type SelectProProps<T extends { value: string; label: string }> = {
//   formik: any;
//   name: string;
//   label?: React.ReactNode;
//   required?: boolean;
//   options: T[];
//   value: T | null;
//   placeholder?: string;
//   menuMaxHeight?: number;
// menuPortalZIndex?: number;

//   // NEW (optional) – for edit screens / parity with native inputs
//   onChange?: (opt: T | null) => void;
//   onBlur?: (e: React.FocusEvent<any>) => void;

//   // NEW (optional) – both spellings supported
//   disabled?: boolean;
//   isDisabled?: boolean; // alias for compatibility
// };
// type BaseFieldPropsPro = {
//   formik: ReturnType<typeof useFormik<any>>;
//   name: string;
//   label: React.ReactNode;             // allow rich labels
//   required?: boolean;
//   placeholder?: string;
//   disabled?: boolean;
// };

// // type SelectProProps = BaseFieldPropsPro & {
// //   options: Array<{ value: string; label: string }>;
// //   /** Optional controlled value; if omitted, binds to formik[name] */
// //   value?: { value: string; label: string } | null;
// //   /** Optional external onChange; formik will still be updated if name exists */
// //   onChange?: (opt: { value: string; label: string } | null) => void;
// //   menuMaxHeight?: number;
// //   menuPortalZIndex?: number;
// // };


// const SELECT_HEIGHT = 39;

// const commonTheme = (t: any) => ({
//   ...t,
//   colors: {
//     ...t.colors,
//     primary: "#9ca3af",
//     primary25: "rgba(156,163,175,0.15)",
//     primary50: "rgba(156,163,175,0.25)",
//   },
// });

// const centeredCommonStyles = (hasError: boolean) => ({
//   control: (base: any, state: any) => ({
//     ...base,
//     minHeight: SELECT_HEIGHT,
//     height: SELECT_HEIGHT,           // fixed for single-select
//     borderRadius: 10,
//     alignItems: "center",
//     backgroundColor: state.isFocused ? "#fafafa" : "#f5f6fa",
//     borderWidth: 2,
//     borderStyle: "solid",
//     borderColor: hasError ? "#747981" : state.isFocused ? "#9ca3af" : "#c9ced6",
//     boxShadow: "none",
//     outline: "none",
//     transition: "all .2s ease-in-out",
//     ":hover": {
//       borderColor: hasError ? "#747981" : "#9ca3af",
//       backgroundColor: "#fafafa",
//     },
//   }),
//   valueContainer: (b: any) => ({
//     ...b,
//     padding: "0 12px",
//     minHeight: SELECT_HEIGHT,
//     display: "flex",
//     alignItems: "center",
//   }),
//   indicatorsContainer: (b: any) => ({
//     ...b,
//     paddingRight: 10,
//     minHeight: SELECT_HEIGHT,
//     display: "flex",
//     alignItems: "center",
//   }),
//   dropdownIndicator: (provided: any) => ({
//     ...provided,
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "center",
//     height: "100%",
//   }),
//   indicatorSeparator: () => ({ display: "none" }),
//   placeholder: (b: any) => ({
//     ...b,
//     color: "#6b7280",
//     margin: 0,
//     display: "flex",
//     alignItems: "center",
//   }),
//   singleValue: (b: any) => ({
//     ...b,
//     color: "#111827",
//     margin: 0,
//     display: "flex",
//     alignItems: "center",
//   }),
//   input: (b: any) => ({
//     ...b,
//     margin: 0,
//     padding: 0,
//   }),
//   menu: (b: any) => ({ ...b, borderRadius: 10, overflow: "hidden" }),
// });





// const DropdownIndicator = (props: any) => {
//     return (
//         <components.DropdownIndicator {...props}>
//             <i className="ri-arrow-down-s-line fs-5" style={{ color: '#6c757d' }}></i>
//         </components.DropdownIndicator>
//     );
// };

// type InputProProps = BaseFieldPropsPro & {
//   type?: React.HTMLInputTypeAttribute;
// };
// export function UIReactSelectProEdit<T extends { value: string; label: string }>(
//   {
//     formik,
//     name,
//     label,
//     required,
//     options,
//     value,
//     placeholder = "- Select -",
//     menuMaxHeight = 240,
//     menuPortalZIndex = 20,
//     onChange,
//     onBlur,
//     disabled,
//     isDisabled,
//   }: SelectProProps<T>
// ) {
//   // derive error state from formik
//   const hasError = Boolean(formik.touched?.[name] && formik.errors?.[name]);
//   const error = formik.errors?.[name] as string | undefined;

//   const actuallyDisabled = Boolean(disabled ?? isDisabled);

//   // single, consolidated styles object
//   const styles = {
//     ...centeredCommonStyles(hasError),
//     menuPortal: (base: any) => ({
//       ...base,
//       position: "absolute",
//       zIndex: menuPortalZIndex, // above cards/modals
//       inset: "auto",
//       overflow: "visible",
//     }),
//     menu: (b: any) => ({
//       ...centeredCommonStyles(hasError).menu(b),
//       maxHeight: menuMaxHeight,
//       overflowY: "auto",
//       marginTop: 4,
//     }),
//   };

//   return (
//     <div className="mb-3">
//       {label && (
//         <label className="form-label" htmlFor={name}>
//           {label} {required && <span className="text-danger">*</span>}
//         </label>
//       )}

//       <Select<T, false>
//         inputId={name}
//         name={name}
//         classNamePrefix="rs"
//         value={value}
//         options={options}
//         placeholder={placeholder}
//         isDisabled={actuallyDisabled}
//         styles={styles}
//         theme={commonTheme}
//         components={{ DropdownIndicator }}
//         onChange={(opt) => {
//           // keep Formik in sync with just the value
//           formik.setFieldValue(name, (opt as any)?.value ?? "");
//           // bubble whole option if caller wants it
//           onChange?.(opt as T | null);
//         }}
//         onBlur={(e) => {
//           // mark touched for validation
//           formik.setFieldTouched(name, true, true);
//           // bubble blur back to caller if provided
//           onBlur?.(e);
//         }}
//       />

//       {hasError ? <div className="invalid-feedback d-block">{error}</div> : null}
//     </div>
//   );
// }

// // OrdersSelectInput.tsx  — replace UIReactSelectPro with this version
// export function UIReactSelectPro<T extends OptionBase>({
//   formik,
//   name,
//   label,
//   required,
//   placeholder,
//   disabled,
//   options,
//   value,
//   onChange,
//   menuMaxHeight,
//   menuPortalZIndex,
// }: SelectProPropsAdd<T>) {
//   const touched = (formik.touched as any)?.[name];
//   const error = (formik.errors as any)?.[name];
//   const hasError = !!(touched && error);

//   // derive from formik if uncontrolled
//   const derived: T | null =
//     value !== undefined
//       ? value
//       : (options.find((o) => o.value === (formik.values as any)[name]) as T) || null;

//   // anchor so the menu renders exactly under the control
//   const anchorRef = React.useRef<HTMLDivElement>(null);
//   const anchorW = anchorRef.current?.offsetWidth;

//   return (
//     <div className="mb-3 rs-anchor" ref={anchorRef}>
//       <label className="form-label">
//         {label} {required && <span className="text-danger">*</span>}
//       </label>

//       <Select<T, false>
//         inputId={name}
//         name={name}
//         options={options}
//         value={derived}
//         isDisabled={!!disabled}
//         onChange={(opt) => {
//           const sel = (opt ?? null) as T | null;
//           onChange?.(sel);                         // ← returns the exact T (e.g., with cost)
//           formik.setFieldValue(name, sel?.value || "");
//         }}
//         onBlur={() => formik.setFieldTouched(name, true)}
//         placeholder={placeholder || "- Select -"}
//         classNamePrefix="rs"

//         /* anchored menu */
//         menuPortalTarget={anchorRef.current ?? undefined}
//         menuPosition="absolute"
//         menuPlacement="bottom"
//         menuShouldScrollIntoView={false}

//         /* full-width dropdown */
//          styles={{
//           ...centeredCommonStyles(hasError),
//           menuPortal: (base: any) => ({
//             ...base,
//             position: "absolute",
//             zIndex: menuPortalZIndex ?? 20, // above cards
//             inset: "auto",                  // let react-select set top/left
//             overflow: "visible",
//           }),
//           menu: (b: any) => ({
//             ...centeredCommonStyles(hasError).menu(b),
//             maxHeight: menuMaxHeight ?? 240,
//             overflowY: "auto",
//             marginTop: 4,
//           }),
//         }}
//         theme={commonTheme}
//         components={{ DropdownIndicator }}
//       />

//       {hasError ? <div className="invalid-feedback d-block">{error}</div> : null}
//     </div>
//   );
// }


// // export function UIReactSelectPro({
// //   formik,
// //   name,
// //   label,
// //   required,
// //   placeholder,
// //   disabled,
// //   options,
// //   value,
// //   onChange,
// //   menuMaxHeight,
// //   menuPortalZIndex,
// // }: SelectProProps) {
// //   const touched = (formik.touched as any)?.[name];
// //   const error = (formik.errors as any)?.[name];
// //   const hasError = !!(touched && error);

// //   // derive value from formik if not controlled
// //   const derived =
// //     value !== undefined
// //       ? value
// //       : options.find((o) => o.value === (formik.values as any)[name]) || null;

// //   // ⬇️ anchor element the menu will portal into
// //   const anchorRef = React.useRef<HTMLDivElement>(null);

// //   return (
// //     <div className="mb-3 rs-anchor" ref={anchorRef}>
// //       <label className="form-label">
// //         {label} {required && <span className="text-danger">*</span>}
// //       </label>

// //       <Select
// //         inputId={name}
// //         name={name}
// //         options={options}
// //         value={derived}
// //         isDisabled={!!disabled}
// //         onChange={(opt: any) => {
// //           onChange?.(opt ?? null);
// //           if (name) formik.setFieldValue(name, opt?.value || "");
// //         }}
// //         onBlur={() => formik.setFieldTouched(name, true)}
// //         placeholder={placeholder || "- Select -"}
// //         classNamePrefix="rs"

// //         /* ✅ anchor the menu to this field (no drift) */
// //         menuPortalTarget={anchorRef.current ?? undefined}
// //         menuPosition="absolute"
// //         menuPlacement="bottom"
// //         menuShouldScrollIntoView={false}

// //         /* styles */
//         // styles={{
//         //   ...centeredCommonStyles(hasError),
//         //   menuPortal: (base: any) => ({
//         //     ...base,
//         //     position: "absolute",
//         //     zIndex: menuPortalZIndex ?? 20, // above cards
//         //     inset: "auto",                  // let react-select set top/left
//         //     overflow: "visible",
//         //   }),
//         //   menu: (b: any) => ({
//         //     ...centeredCommonStyles(hasError).menu(b),
//         //     maxHeight: menuMaxHeight ?? 240,
//         //     overflowY: "auto",
//         //     marginTop: 4,
//         //   }),
//         // }}
// //         theme={commonTheme}
// //         components={{ DropdownIndicator }}
// //       />

// //       {hasError ? <div className="invalid-feedback d-block">{error}</div> : null}
// //     </div>
// //   );
// // }


// // keep this
// type BaseFieldPropsProEdit = {
//   formik: ReturnType<typeof useFormik<any>>;
//   name: string;
//   label: React.ReactNode;
//   required?: boolean;
//   placeholder?: string;
//   disabled?: boolean;
//     type?: React.HTMLInputTypeAttribute;

//   // ✅ optional controlled props for edit screens
//   value?: string;
//   onChange?: React.ChangeEventHandler<HTMLInputElement>;
//   onBlur?: React.FocusEventHandler<HTMLInputElement>;
//   readOnly?: boolean;
// };

// // ⬇️ extend props to support edit use-cases
// type InputProPropsEdit = BaseFieldPropsProEdit & {
//   type?: React.HTMLInputTypeAttribute;
//   value?: string; // optional controlled value
//   onChange?: React.ChangeEventHandler<HTMLInputElement>;
//   onBlur?: React.FocusEventHandler<HTMLInputElement>;
//   readOnly?: boolean; // edit mode without disabling styles
  
// };

// export function UIInputProEdit({
//   formik,
//   name,
//   label,
//   required,
//   placeholder,
//   disabled,
//   readOnly,
//   type = "text",
//   value,
//   onChange,
//   onBlur,
// }: InputProPropsEdit) {
//   const [hover, setHover] = React.useState(false);
//   const [focus, setFocus] = React.useState(false);

//   const touched = (formik.touched as any)?.[name];
//   const error = (formik.errors as any)?.[name];
//   const hasError = !!(touched && error);

//   const bgColor = hover || focus ? "#fafafa" : "#f5f6fa";
//   const borderColor = hasError ? "#747981" : hover || focus ? "#9ca3af" : "#c9ced6";

//   const style: React.CSSProperties = {
//     width: "100%",
//     height: 39,
//     borderRadius: 10,
//     padding: "10px 12px",
//     borderWidth: 2,
//     borderStyle: "solid",
//     borderColor,
//     backgroundColor: bgColor,
//     outline: "none",
//     transition: "all .2s ease-in-out",
//     boxShadow: "none",
//     color: "#111827",
//   };

//   // prefer controlled value if provided; otherwise use Formik state
//   const resolvedValue =
//     value !== undefined ? value : (formik.values as any)?.[name] ?? "";

//   return (
//     <div className="mb-3">
//       <label className="form-label">
//         {label} {required && <span className="text-danger">*</span>}
//       </label>

//       <input
//         id={name}
//         name={name}
//         className={`form-control ${hasError ? "is-invalid" : ""}`}
//         style={style}
//         type={type}
//         value={resolvedValue}
//         onChange={(e) => {
//           // bubble to external handler if provided
//           onChange?.(e);
//           // keep Formik in sync unless the caller fully controls the field
//           if (onChange == null) formik.handleChange(e);
//         }}
//         onFocus={() => setFocus(true)}
//         onBlur={(e) => {
//           setFocus(false);
//           // bubble first
//           onBlur?.(e);
//           // keep Formik touched state unless caller overrides
//           if (onBlur == null) formik.handleBlur(e);
//         }}
//         onMouseEnter={() => setHover(true)}
//         onMouseLeave={() => setHover(false)}
//         placeholder={placeholder}
//         aria-invalid={hasError}
//         disabled={!!disabled}
//         readOnly={!!readOnly}
//         aria-readonly={!!readOnly}
//       />

//       {hasError ? <div className="invalid-feedback d-block">{error}</div> : null}
//     </div>
//   );
// }


// export function UIInputPro({
//   formik,
//   name,
//   label,
//   required,
//   placeholder,
//   disabled,
//   type = "text",
// }: InputProProps) {
//   const [hover, setHover] = React.useState(false);
//   const [focus, setFocus] = React.useState(false);

//   const touched = (formik.touched as any)?.[name];
//   const error = (formik.errors as any)?.[name];
//   const hasError = !!(touched && error);

//   const bgColor = hover || focus ? "#fafafa" : "#f5f6fa";
//   const borderColor = hasError ? "#747981" : hover || focus ? "#9ca3af" : "#c9ced6";

//   const style: React.CSSProperties = {
//     width: "100%",
//     height: 39,
//     borderRadius: 10,
//     padding: "10px 12px",
//     borderWidth: 2,
//     borderStyle: "solid",
//     borderColor,
//     backgroundColor: bgColor,
//     outline: "none",
//     transition: "all .2s ease-in-out",
//     boxShadow: "none",
//     color: "#111827",
//   };

//   return (
//     <div className="mb-3">
//       <label className="form-label">
//         {label} {required && <span className="text-danger">*</span>}
//       </label>

//       <input
//         id={name}
//         name={name}
//         className={`form-control ${hasError ? "is-invalid" : ""}`}
//         style={style}
//         type={type}
//         value={(formik.values as any)[name] ?? ""}
//         onChange={formik.handleChange}
//         onFocus={() => setFocus(true)}
//         onBlur={(e) => {
//           setFocus(false);
//           formik.handleBlur(e);
//         }}
//         onMouseEnter={() => setHover(true)}
//         onMouseLeave={() => setHover(false)}
//         placeholder={placeholder}
//         aria-invalid={hasError}
//         disabled={!!disabled}
//       />

//       {hasError ? <div className="invalid-feedback d-block">{error}</div> : null}
//     </div>
//   );
// }

export function UIInputPro({
  formik,
  name,
  label,
  required,
  placeholder,
  disabled,
  readOnly,
  type = "text",
  value,
  onChange,
  onBlur,
}: InputProProps) {
  const [hover, setHover] = React.useState(false);
  const [focus, setFocus] = React.useState(false);

  const touched = (formik.touched as any)?.[name];
  const error = (formik.errors as any)?.[name];

  // Prefer controlled value if provided; otherwise, use Formik state
  const resolvedValue =
    value !== undefined ? value : (formik.values as any)?.[name] ?? "";

  // NEW: if the field has a value, don't visually show the stale error
  const isEmpty = String(resolvedValue ?? "").trim() === "";
  const hasError = Boolean(touched && error && isEmpty);

  // NEW: clear stale error once a valid value appears (no user blur needed)
  React.useEffect(() => {
    if (touched && error && !isEmpty) {
      formik.setFieldError(name, undefined);
    }
    // eslint-disable-next-line
  }, [resolvedValue]);

  const bgColor = hover || focus ? "#fafafa" : "#f5f6fa";
  const borderColor = hasError ? "#747981" : hover || focus ? "#9ca3af" : "#c9ced6";

  const style: React.CSSProperties = {
    width: "100%",
    height: 39,
    borderRadius: 10,
    padding: "10px 12px",
    borderWidth: 2,
    borderStyle: "solid",
    borderColor,
    backgroundColor: bgColor,
    outline: "none",
    transition: "all .2s ease-in-out",
    boxShadow: "none",
    color: "#111827",
  };
  const suppressEmailSuggestions = isEmailInput(name, type);

  return (
    <div className="mb-3">
      {label && (
        <label className="form-label" htmlFor={name}>
          {label} {required && <span className="text-danger">*</span>}
        </label>
      )}

      <input
        id={name}
        name={name}
        className={`form-control ${hasError ? "is-invalid" : ""}`}
        style={style}
        type={type}
        value={resolvedValue}
        onChange={(e) => {
          onChange?.(e);
          if (!onChange) formik.handleChange(e);
        }}
        onFocus={() => setFocus(true)}
        onBlur={(e) => {
          setFocus(false);
          onBlur?.(e);
          if (!onBlur) formik.handleBlur(e);
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        placeholder={placeholder}
        aria-invalid={hasError}
        disabled={!!disabled}
        readOnly={!!readOnly}
        aria-readonly={!!readOnly}
        autoComplete={suppressEmailSuggestions ? "nope" : "off"}
        spellCheck={suppressEmailSuggestions ? false : undefined}
        autoCorrect={suppressEmailSuggestions ? "off" : undefined}
        autoCapitalize={suppressEmailSuggestions ? "off" : undefined}
        data-lpignore={suppressEmailSuggestions ? "true" : undefined}
        data-bwignore={suppressEmailSuggestions ? "true" : undefined}
        {...(suppressEmailSuggestions ? { "data-1p-ignore": "true" } : {})}
      />

      {hasError ? <div className="invalid-feedback d-block">{error}</div> : null}
    </div>
  );
}

